import { type preferredTimeDisplayModeTypes } from 'types/customer.types';
import { DATE_FORMAT } from 'utils/constants/dateTime';
import { convertUtcToDisplayTime } from 'utils/helpers/dateTime';

/* eslint-disable @typescript-eslint/restrict-plus-operands */
export const MAX_CHART_ITEMS = 50;
export const MAX_TYPES_TO_SHOW = 10;

const prefixesToTrimFromDTC = ['CDL', 'j1939', 'j1708', 'kj1939', 'J1939'];
const suffixesToTrimFromDTC = ['DTC'];

export function cleanDTCString(input: string) {
  let cleaned = input;

  for (const prefix of prefixesToTrimFromDTC) {
    if (cleaned.startsWith(prefix)) {
      cleaned = cleaned.slice(prefix.length);
      break;
    }
  }

  for (const suffix of suffixesToTrimFromDTC) {
    if (cleaned.endsWith(suffix)) {
      cleaned = cleaned.slice(0, -suffix.length);
      break;
    }
  }

  return cleaned.trim();
}

export const processAssetChartData = (
  data: any[],
  uniqueDtcTypes: any,
  limit = MAX_CHART_ITEMS
) => {
  const assetGroups: any = {};
  data.forEach((item) => {
    if (!assetGroups[item.asset]) {
      assetGroups[item.asset] = {
        name: item.asset,
        total: 0,
      };

      uniqueDtcTypes.forEach((type: any) => {
        assetGroups[item.asset][type] = 0;
      });
    }

    assetGroups[item.asset][item.dtc_type] += 1;
    assetGroups[item.asset].total += 1;
  });

  let formattedData: any = Object.values(assetGroups).sort(
    (a: any, b: any) => b.total - a.total
  );

  if (formattedData.length > limit) {
    formattedData = formattedData.slice(0, limit);
  }

  return formattedData;
};

export const processTypeChartData = (
  data: any[],
  uniqueDtcTypes: any,
  limit = MAX_CHART_ITEMS
) => {
  const assetGroups: any = {};
  data.forEach((item) => {
    if (!assetGroups[item.dtc_type]) {
      assetGroups[item.dtc_type] = {
        name: item.dtc_type,
        total: 0,
      };

      uniqueDtcTypes.forEach((type: any) => {
        assetGroups[item.dtc_type][type] = 0;
      });
    }

    assetGroups[item.dtc_type][item.dtc_code] += 1;
    assetGroups[item.dtc_type].total += 1;
  });

  let formattedData: any = Object.values(assetGroups).sort(
    (a: any, b: any) => b.total - a.total
  );

  if (formattedData.length > limit) {
    formattedData = formattedData.slice(0, limit);
  }

  return formattedData;
};

export const processDailyChartData = (
  data: any[],
  uniqueDtcTypes: any,
  preferredTimeDisplayMode: preferredTimeDisplayModeTypes,
  timezone: string,
  maxDays = MAX_CHART_ITEMS
) => {
  const dailyFilteredData = [...data];

  const dailyGroups: any = {};
  dailyFilteredData.forEach((item) => {
    const dateStr = convertUtcToDisplayTime(
      item.start_ts,
      preferredTimeDisplayMode,
      timezone,
      DATE_FORMAT
    );

    if (!dailyGroups[dateStr]) {
      dailyGroups[dateStr] = {
        name: dateStr,
        total: 0,
      };

      uniqueDtcTypes.forEach((type: any) => {
        dailyGroups[dateStr][type] = 0;
      });
    }

    dailyGroups[dateStr][item.dtc_type] += 1;
    dailyGroups[dateStr].total += 1;
  });

  let formattedData: any = Object.values(dailyGroups).sort((a: any, b: any) =>
    a.name.localeCompare(b.name)
  );

  if (formattedData.length > maxDays) {
    formattedData = aggregateByWeek(formattedData, uniqueDtcTypes);
  }

  return formattedData;
};

export const processDailyDtcCodeData = (
  data: any[],
  uniqueDtcCodes: any,
  preferredTimeDisplayMode: preferredTimeDisplayModeTypes,
  timezone: string,
  maxDays = MAX_CHART_ITEMS
) => {
  const dailyFilteredData = [...data];

  const dailyGroups: any = {};
  dailyFilteredData.forEach((item) => {
    const dateStr = convertUtcToDisplayTime(
      item.start_ts,
      preferredTimeDisplayMode,
      timezone,
      DATE_FORMAT
    );

    if (!dailyGroups[dateStr]) {
      dailyGroups[dateStr] = {
        name: dateStr,
        total: 0,
      };

      uniqueDtcCodes.forEach((code: any) => {
        dailyGroups[dateStr][code] = 0;
      });
    }

    const code = item.dtc_code;
    if (uniqueDtcCodes.includes(code)) {
      dailyGroups[dateStr][code] += 1;
    }

    dailyGroups[dateStr].total += 1;
  });

  let formattedData: any = Object.values(dailyGroups).sort((a: any, b: any) =>
    a.name.localeCompare(b.name)
  );

  if (formattedData.length > maxDays) {
    formattedData = aggregateByWeek(formattedData, uniqueDtcCodes);
  }

  return formattedData;
};

const aggregateByWeek = (dailyData: any[], types: string[]) => {
  const weekMap: any = {};

  dailyData.forEach((day) => {
    const [dayPart, monthPart, yearPart] = day.name.split('/');
    const date = new Date(
      parseInt(yearPart),
      parseInt(monthPart) - 1,
      parseInt(dayPart)
    );

    const dayOfWeek = date.getDay();
    const diff = date.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const weekStart = new Date(date.setDate(diff));

    const weekKey = `${String(weekStart.getDate()).padStart(2, '0')}/${String(
      weekStart.getMonth() + 1
    ).padStart(2, '0')}/${weekStart.getFullYear()}`;

    if (!weekMap[weekKey]) {
      weekMap[weekKey] = {
        name: `Week of ${weekKey}`,
        total: 0,
      };

      types.forEach((type: any) => {
        weekMap[weekKey][type] = 0;
      });
    }

    types.forEach((type: any) => {
      if (day[type]) {
        weekMap[weekKey][type] += day[type];
      }
    });

    weekMap[weekKey].total += day.total || 0;
  });

  return Object.values(weekMap).sort((a: any, b: any) =>
    a.name.localeCompare(b.name)
  );
};

export const getLimitedTypes = (
  types: string[],
  data: any[],
  limit = MAX_TYPES_TO_SHOW
) => {
  const typeCounts: Record<string, number> = {};

  data.forEach((item) => {
    const type = item.dtc_type || item.dtc_code;
    typeCounts[type] = (typeCounts[type] || 0) + 1;
  });

  return types
    .sort((a, b) => (typeCounts[b] || 0) - (typeCounts[a] || 0))
    .slice(0, limit);
};
